import type { DruckverlustTeilstrecke, LueftungRaumschallzeile, SchallAnlage } from '../../api';
import { formatiereZahl, type Vorbehalt } from '../../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TV6b - DIE REGELN DER SEITEN „SCHALL“ UND „RAUMSCHALL“ (ohne React)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Vorbehalte je Weg und je Raum (Schubfächer), der Zielpegel je Strang und
 * die Texte, die beide Seiten teilen. Den Kasten über den Zahlen zeigen die
 * Seiten seit TV7a-2 (§22 c) aus der Antwort ihres Berichts LUE-SCH bzw. LUE-RSC
 * (`LueftungBerichtsvorbehalte.tsx`), Satz für Satz wie im PDF - bis dahin
 * stand er hier, aus anderen Zahlen als der Bericht (Befund §19.6).
 */

/** Die Oktavmitten nach DIN EN ISO 266 (die Reihenfolge der Spektren in der API). */
export const BAENDER = [63, 125, 250, 500, 1000, 2000, 4000, 8000] as const;

/**
 * Der Zielpegel eines Strangs, dessen Raum keine Nutzungsart trägt. Er ist der
 * strengste Wert in `lueftung_raumakustik` und damit die sichere Richtung.
 */
export const ZIEL_RUECKFALL = 35;

/** Der Zielpegel eines Strangs - aus der Nutzungsart seines Raums, sonst der Rückfall. */
export function ziel(t: Pick<DruckverlustTeilstrecke, 'zielpegelDbA'>): number {
  return t.zielpegelDbA ?? ZIEL_RUECKFALL;
}

const n = (wert: number) => formatiereZahl(wert, 0);

/** Über einem Weg: fehlende Kennwerte und die Stränge, die gegen den Rückfall-Zielpegel stehen. */
export function wegVorbehalte(a: SchallAnlage, straenge: DruckverlustTeilstrecke[]): Vorbehalt[] {
  const v: Vorbehalt[] = [];
  if (a.ohneKennwerte) {
    v.push({
      art: 'sonstiges',
      text: 'Auf diesem Weg fehlen Kennwerte - der Pegel ist eine untere Schranke, kein Nachweis.',
    });
  }
  const ohneZiel = straenge.filter((t) => t.lpaDirektDb !== null && t.zielpegelDbA === null).length;
  if (ohneZiel > 0) {
    v.push({
      art: 'sonstiges',
      text: `${n(ohneZiel)} Strang/Stränge enden in einem Raum ohne Nutzungsart: Sie stehen gegen den Rückfall `
        + `${n(ZIEL_RUECKFALL)} dB(A), den strengsten Zielpegel.`,
    });
  }
  return v;
}

/** Über einem Raum: unsichere Zuordnung, nicht berechenbar, untere Schranke. */
export function raumVorbehalte(r: LueftungRaumschallzeile): Vorbehalt[] {
  const v: Vorbehalt[] = [];
  if (r.zuordnungUnsicher) {
    v.push({
      art: 'sonstiges',
      text: 'Raumzuordnung unsicher: Die Auslässe sind über die Lage zugeordnet und passen nicht zu diesem Raum. '
        + 'Der Pegel wird gerechnet, aber nicht beurteilt - eine Überschreitung aus einer falschen Zuordnung ist '
        + 'keine Überschreitung.',
    });
  }
  if (r.nichtBerechenbar) {
    v.push({
      art: 'sonstiges',
      text: `Nicht berechenbar: Keiner der ${n(r.anzahlAuslaesse)} Auslässe trägt ein Kennfeld-Eigengeräusch, und `
        + 'der über den Kanal gedämpfte Geräteschall liegt bei oder unter 0 dB(A). Gebraucht wird der Kennwert der '
        + 'Baureihe, nicht ein Ersatzwert.',
    });
  }
  if (r.istUntereSchranke) {
    v.push({
      art: 'sonstiges',
      text: `Untere Schranke: ${n(r.auslaesseOhneEigengeraeusch)} von ${n(r.anzahlAuslaesse)} Auslässen gehen ohne `
        + 'eigenes Strömungsgeräusch in die Kette; der wirkliche Pegel liegt über dem ausgewiesenen.',
    });
  }
  return v;
}

/** Was den Pegel am lautesten Auslass bestimmt - und was dagegen hilft. */
export function massgeblichText(massgeblich: string | null | undefined): string {
  if (massgeblich === 'Auslass') {
    return 'Das Eigengeräusch des Auslasses bestimmt den Pegel. Ein Schalldämpfer im Kanal hilft hier nicht - nur '
      + 'ein anderer Auslass oder ein kleinerer Volumenstrom je Auslass.';
  }
  if (massgeblich === 'Gerät') {
    return 'Das Gerät bestimmt den Pegel; die Dämpfung im Weg reicht nicht aus. Hier wirkt ein Kulissenschalldämpfer.';
  }
  return 'Das Netz bestimmt den Pegel. Dämpfung und Aufteilung im Kanal tragen den größten Teil.';
}

/** Ein Spektrum als Zeilen je Oktavband (für `DataTable`); fehlt ein Band, bleibt es eine Fehlstelle. */
export function spektrum(
  reihen: Record<string, readonly (number | null)[] | null | undefined>,
): ({ band: number } & Record<string, number | null>)[] {
  return BAENDER.map((band, i) => {
    const zeile: { band: number } & Record<string, number | null> = { band };
    for (const [name, werte] of Object.entries(reihen)) zeile[name] = werte?.[i] ?? null;
    return zeile;
  });
}

/** Die Überschreitung mit Vorzeichen, deutsch: „+3,4“. */
export function ueberschreitungText(dB: number): string {
  return `+${formatiereZahl(dB, 1)}`;
}
